import { SidebarSectionType, StepTipType, ZoneType } from 'data/types';
import { getZoneStepMissingReason } from 'utils/configuration-step-reasons';
import { ZoneStepSectionsInput } from 'utils/zone-step-sections-types';

const ZONE_STEP_DESCRIPTION = 'Click rooms to add them to a zone.';

export function zoneStepId(zoneNumber: number): string {
  return `add_spaces_zone_${zoneNumber}`;
}

function zoneStepTitle(zoneNumber: number): string {
  return `Create Zone ${zoneNumber}`;
}

function completedZoneSection(
  zone: ZoneType,
  zoneNumber: number,
  tips: StepTipType[] | undefined
): SidebarSectionType {
  const stepId = zoneStepId(zoneNumber);
  const title = zoneStepTitle(zoneNumber);
  return {
    id: stepId,
    title,
    description: ZONE_STEP_DESCRIPTION,
    progressBarLabel: 'Zones',
    progressBarGroup: 'zones',
    requirementsMet: true,
    required: false,
    step_code: 'zones',
    steps: [
      {
        id: stepId,
        title,
        description: ZONE_STEP_DESCRIPTION,
        status: 'complete',
        tips,
        zoneUniqueId: zone.uniqueId
      }
    ]
  };
}

type OpenZoneSectionInput = {
  zone: ZoneType | undefined;
  zoneNumber: number;
  canStartZones: boolean;
  cannotStartReason: string;
  tips: StepTipType[] | undefined;
};

function openZoneSection({
  zone,
  zoneNumber,
  canStartZones,
  cannotStartReason,
  tips
}: OpenZoneSectionInput): SidebarSectionType {
  const stepId = zoneStepId(zoneNumber);
  const title = zoneStepTitle(zoneNumber);
  const isFirstZone = zoneNumber === 1;
  return {
    id: stepId,
    title,
    description: ZONE_STEP_DESCRIPTION,
    progressBarLabel: 'Zones',
    progressBarGroup: 'zones',
    requirementsMet: false,
    requirementsMissingReason: getZoneStepMissingReason({
      canStartZones,
      cannotStartReason,
      isFirstZone,
      currentZoneHasSpaces: false
    }),
    required: false,
    step_code: 'zones',
    ...(zone ? {} : { disabled: !canStartZones }),
    steps: [
      {
        id: stepId,
        title,
        description: ZONE_STEP_DESCRIPTION,
        status: 'incomplete',
        tips,
        enableDoneButton: true,
        doneButtonDisabled: isFirstZone,
        ...(zone ? { zoneUniqueId: zone.uniqueId } : {})
      }
    ]
  };
}

export function buildZoneStepSections({
  zonesWithItems,
  currentEmptyZone,
  hasUnzonedSpaces,
  canStartZones,
  cannotStartReason,
  allowNewZone,
  tips
}: ZoneStepSectionsInput): SidebarSectionType[] {
  const sections = zonesWithItems.map((zone, index) =>
    completedZoneSection(zone, index + 1, tips)
  );

  if (currentEmptyZone && hasUnzonedSpaces && allowNewZone) {
    sections.push(
      openZoneSection({
        zone: currentEmptyZone,
        zoneNumber: zonesWithItems.length + 1,
        canStartZones,
        cannotStartReason,
        tips
      })
    );
  }

  if (sections.length === 0) {
    sections.push(
      openZoneSection({
        zone: undefined,
        zoneNumber: 1,
        canStartZones,
        cannotStartReason,
        tips
      })
    );
  }

  return sections;
}
